import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Circle, Loader2, X } from 'lucide-react'
import type { TodoItem } from '../../api/types'
import { cn } from '../../lib/cn'

/**
 * The session's task list, rendered as the last block of the chat feed — the
 * same slot as the `question` card, not the composer. One hairline card headed
 * by the list's name and a done/total counter; the header collapses the list.
 * Rows are monochrome on purpose: done and cancelled are struck through and
 * grey, the running row is just heavier text, no tint, no tag. A finished list
 * stays on screen until the turn ends, when the server clears it.
 */
export function ChatTodos({ todos }: { todos?: TodoItem[] }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)

  if (!todos || todos.length === 0) return null

  const total = todos.length
  const completed = todos.filter(todo => todo.status === 'completed').length
  const active = todos.find(todo => todo.status === 'in_progress')
  const statusLabel: Record<TodoItem['status'], string> = {
    pending: t('chat.todoStatus_pending'),
    in_progress: t('chat.todoStatus_in_progress'),
    completed: t('chat.todoStatus_completed'),
    cancelled: t('chat.todoStatus_cancelled')
  }

  return (
    <section
      aria-label={t('chat.todoList')}
      className="overflow-hidden rounded-[10px] border border-line bg-canvas"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        className="flex w-full items-center gap-1.5 px-2.5 pb-1.5 pt-2 text-left"
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-ink-500 transition-transform',
            !open && '-rotate-90'
          )}
          aria-hidden="true"
        />
        <span className="shrink-0 text-[12.5px] font-medium text-ink-300">{t('chat.todoList')}</span>
        {/* Collapsed, the header keeps naming the running task so progress
            stays readable without expanding the list. */}
        {!open && active && (
          <span className="flex min-w-0 items-center gap-1 text-[12.5px] text-ink-400">
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
            <span className="truncate">{active.content}</span>
          </span>
        )}
        <span className="ml-auto shrink-0 font-mono text-[11px] tabular-nums text-ink-500">{completed}/{total}</span>
      </button>

      {open && (
        <ul className="max-h-40 overflow-y-auto px-2.5 pb-1.5">
          {todos.map((todo, idx) => {
            const isCompleted = todo.status === 'completed'
            const isInProgress = todo.status === 'in_progress'
            const isCancelled = todo.status === 'cancelled'

            return (
              <li
                key={idx}
                aria-current={isInProgress ? 'step' : undefined}
                className="flex items-start gap-1.5 py-0.5 text-[12.5px] leading-[18px]"
              >
                <span
                  className={cn('mt-0.5 shrink-0 text-ink-400', isInProgress && 'text-ink-300')}
                  aria-hidden="true"
                >
                  {isCompleted && <Check className="h-3.5 w-3.5" />}
                  {isInProgress && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {isCancelled && <X className="h-3.5 w-3.5" />}
                  {todo.status === 'pending' && <Circle className="h-3.5 w-3.5" />}
                </span>

                <p
                  className={cn(
                    'min-w-0 flex-1 break-words',
                    isCompleted && 'text-ink-500 line-through',
                    isCancelled && 'text-ink-500 line-through',
                    isInProgress && 'font-medium text-ink-200',
                    todo.status === 'pending' && 'text-ink-400'
                  )}
                >
                  <span className="sr-only">{statusLabel[todo.status]}: </span>
                  {todo.content}
                </p>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
